import { SearchableSelect } from '@st/theme'
import type { Jurisdiction as CatalogEntry } from '@st/sdk'
import { match } from '@st/pattern'
import { inputClass, type Control, type InputProps } from '~/st-doc-viewer/schema-editor/controls'

type Jurisdiction = { level: string; code: string | null; name: string }
const local: CatalogEntry = {
  identifier: 'local',
  level: 'local',
  name: 'Other local jurisdiction'
}

function JurisdictionInput({
  value,
  label,
  onChange,
  catalog
}: InputProps & { catalog: CatalogEntry[] }) {
  const current = (value ?? {}) as Partial<Jurisdiction>
  const selected = match(current.level)
    .with('local', () => local.identifier)
    .otherwise(() => current.code ?? '')
  return (
    <span className="block space-y-2">
      <SearchableSelect
        title="Jurisdiction"
        emptyLabel="No jurisdictions found"
        placeholder="Select jurisdiction"
        ariaLabel={label}
        value={selected}
        options={[...catalog, local]}
        buildValue={(entry) => entry.identifier}
        buildLabel={(entry) => entry.name}
        buildSearchText={(entry) => `${entry.name} ${entry.identifier}`}
        buildTrailing={(entry) => {
          if (entry.level != 'state') return null
          return <span className="text-gray-600">{entry.identifier.replace('US-', '')}</span>
        }}
        onChange={(identifier) => {
          if (identifier == local.identifier) {
            onChange({ level: 'local', code: null, name: '' })
            return
          }
          const entry = catalog.find((entry) => entry.identifier == identifier)!
          onChange({ level: entry.level, code: entry.identifier, name: entry.name })
        }}
      />
      {selected == local.identifier && (
        <input
          className={inputClass}
          aria-label="Local jurisdiction name"
          placeholder="City or locality"
          value={current.name ?? ''}
          onChange={(event) => onChange({ ...current, name: event.target.value })}
        />
      )}
    </span>
  )
}

export function jurisdictionControl(catalog: CatalogEntry[]): Control {
  return {
    render: (props) => <JurisdictionInput {...props} catalog={catalog} />,
    itemLabel: (value) => (value as Jurisdiction | undefined)?.name,
    complete: (value) => {
      const current = value as Jurisdiction | undefined
      if (!current?.name?.trim()) return false
      if (current.level == 'local') return current.code === null
      return catalog.some(
        (entry) =>
          entry.identifier == current.code &&
          entry.level == current.level &&
          entry.name == current.name
      )
    }
  }
}
